/**
 * GRN Table View Component
 * Phase 4: High-density table view for enterprise GRN management
 * 
 * Features:
 * - Dense, scannable data display (25-50 rows visible)
 * - Sortable columns with visual indicators
 * - Row selection for bulk operations
 * - Age-coded visual indicators
 * - Quick actions on hover
 * - Sticky header on scroll
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useMemo } from 'react';
import {
  Package,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Copy,
  Truck,
  Building2,
  FileText,
  ChevronRight,
  MoreHorizontal,
  Check,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import { formatIST } from '../../../utils/timezone';
import { toast } from 'sonner';

// Status configuration
const STATUS_CONFIG = {
  DRAFT: { 
    icon: Clock, 
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    textColor: 'text-gray-600'
  },
  RECEIVED: { 
    icon: Package, 
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    textColor: 'text-blue-600'
  },
  INSPECTING: { 
    icon: ClipboardCheck, 
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    textColor: 'text-amber-600'
  },
  COMPLETED: { 
    icon: CheckCircle, 
    color: 'bg-green-100 text-green-700 border-green-300',
    textColor: 'text-green-600'
  },
  CANCELLED: { 
    icon: XCircle, 
    color: 'bg-red-100 text-red-700 border-red-300',
    textColor: 'text-red-600'
  },
};

// Age category configuration
const AGE_CONFIG = {
  fresh: { 
    label: '<24h', 
    color: 'bg-green-100 text-green-700 border-green-300',
    dotColor: 'bg-green-500'
  },
  warning: { 
    label: '24-48h', 
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    dotColor: 'bg-amber-500'
  },
  overdue: { 
    label: '>48h', 
    color: 'bg-red-100 text-red-700 border-red-300',
    dotColor: 'bg-red-500'
  },
  completed: { 
    label: 'Done', 
    color: 'bg-gray-100 text-gray-500 border-gray-300',
    dotColor: 'bg-gray-400'
  },
};

/**
 * Status Badge - compact version for table
 */
const StatusBadgeCompact = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={cn("h-6 text-xs font-medium gap-1", config.color)}
    >
      <Icon className="w-3 h-3" />
      {status}
    </Badge>
  );
};

/**
 * Age Badge - shows aging with color coding
 */
const AgeBadge = ({ grn }) => {
  const ageHours = grn.age_hours || 0;
  const category = grn.age_category || 'fresh';
  const config = AGE_CONFIG[category] || AGE_CONFIG.fresh;
  
  // Calculate display value
  let displayValue;
  if (category === 'completed') {
    displayValue = '-';
  } else if (ageHours < 24) {
    displayValue = `${Math.round(ageHours)}h`;
  } else {
    const days = Math.floor(ageHours / 24);
    displayValue = `${days}d`;
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div className={cn(
            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium",
            config.color
          )}>
            <span className={cn("w-1.5 h-1.5 rounded-full", config.dotColor)} />
            {displayValue}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{Math.round(ageHours)} hours since creation</p>
          {grn.is_overdue && <p className="text-red-400 text-xs">Overdue (&gt;48h)</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Line Status Summary - compact display of line statuses
 */
const LineStatusSummary = ({ grn }) => {
  const { line_count = 0, lines_accepted = 0, lines_inspecting = 0, lines_rejected = 0, lines_pending = 0 } = grn;
  
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-medium">{line_count}</span>
      {line_count > 0 && (
        <div className="flex items-center gap-1 text-xs">
          {lines_accepted > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-green-600">✓{lines_accepted}</span>
                </TooltipTrigger>
                <TooltipContent>Accepted</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {lines_inspecting > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-amber-600">◐{lines_inspecting}</span>
                </TooltipTrigger>
                <TooltipContent>Inspecting</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {lines_rejected > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-red-600">✗{lines_rejected}</span>
                </TooltipTrigger>
                <TooltipContent>Rejected</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {lines_pending > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-gray-500">○{lines_pending}</span>
                </TooltipTrigger>
                <TooltipContent>Pending</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Sortable Column Header
 */
const SortableHeader = ({ column, label, sortConfig, onSort, className = "" }) => {
  const isActive = sortConfig.column === column;
  const direction = isActive ? sortConfig.direction : null;
  
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "h-8 px-2 -ml-2 font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100",
        isActive && "text-blue-600",
        className
      )}
      onClick={() => onSort(column)}
    >
      {label}
      {isActive ? (
        direction === 'asc' ? (
          <ArrowUp className="ml-1 h-3.5 w-3.5" />
        ) : (
          <ArrowDown className="ml-1 h-3.5 w-3.5" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
};

/**
 * GRN Table View Component
 * 
 * @param {Object} props
 * @param {Array} props.grns - Array of GRN objects
 * @param {Function} props.onSelect - Callback when a GRN is clicked
 * @param {Array} props.selectedIds - Array of selected GRN IDs
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {boolean} props.loading - Loading state
 */
const GRNTableView = ({ 
  grns = [], 
  onSelect, 
  selectedIds = [],
  onSelectionChange,
  loading = false 
}) => {
  // Sort state
  const [sortConfig, setSortConfig] = useState({
    column: 'created_at',
    direction: 'desc'
  });
  
  // Handle sort
  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Sort GRNs
  const sortedGrns = useMemo(() => {
    const sorted = [...grns].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.column) {
        case 'grn_number':
          aVal = a.grn_number || '';
          bVal = b.grn_number || '';
          break;
        case 'created_at':
        case 'receipt_date':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'supplier_name':
          aVal = (a.supplier_name || '').toLowerCase();
          bVal = (b.supplier_name || '').toLowerCase();
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'age_hours':
          aVal = a.age_hours || 0;
          bVal = b.age_hours || 0;
          break;
        case 'line_count':
          aVal = a.line_count || 0;
          bVal = b.line_count || 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [grns, sortConfig]);
  
  // Handle selection
  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(grns.map(g => g.grn_id));
    } else {
      onSelectionChange?.([]);
    }
  };
  
  const handleSelectOne = (grnId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, grnId]);
    } else {
      onSelectionChange?.(selectedIds.filter(id => id !== grnId));
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = (text, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  
  const allSelected = grns.length > 0 && selectedIds.length === grns.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < grns.length;
  
  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="grn-table-view">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-gray-50 sticky top-0 z-10">
            <TableRow className="hover:bg-gray-50">
              {/* Checkbox Column */}
              <TableHead className="w-10 px-3">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                  data-testid="grn-table-select-all"
                />
              </TableHead>
              
              {/* GRN Number */}
              <TableHead className="min-w-[140px]">
                <SortableHeader
                  column="grn_number"
                  label="GRN #"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </TableHead>
              
              {/* Date */}
              <TableHead className="min-w-[100px]">
                <SortableHeader
                  column="created_at"
                  label="Date"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </TableHead>
              
              {/* PO Number */}
              <TableHead className="min-w-[100px]">
                <span className="text-xs font-medium text-gray-600">PO #</span>
              </TableHead>
              
              {/* Supplier */}
              <TableHead className="min-w-[150px]">
                <SortableHeader
                  column="supplier_name"
                  label="Supplier"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </TableHead>
              
              {/* Lines */}
              <TableHead className="w-[100px]">
                <SortableHeader
                  column="line_count"
                  label="Lines"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </TableHead>
              
              {/* Qty */}
              <TableHead className="w-[80px] text-right">
                <span className="text-xs font-medium text-gray-600">Qty</span>
              </TableHead>
              
              {/* Status */}
              <TableHead className="w-[120px]">
                <SortableHeader
                  column="status"
                  label="Status"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </TableHead>
              
              {/* Age */}
              <TableHead className="w-[80px]">
                <SortableHeader
                  column="age_hours"
                  label="Age"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </TableHead>
              
              {/* Actions */}
              <TableHead className="w-[60px] text-right">
                <span className="text-xs font-medium text-gray-600">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          
          <TableBody>
            {sortedGrns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-32 text-center text-gray-500">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No GRNs found</p>
                </TableCell>
              </TableRow>
            ) : (
              sortedGrns.map((grn, index) => {
                const isSelected = selectedIds.includes(grn.grn_id);
                const isOverdue = grn.is_overdue;
                
                return (
                  <TableRow
                    key={grn.grn_id}
                    className={cn(
                      "cursor-pointer transition-colors group",
                      index % 2 === 0 ? "bg-white" : "bg-gray-50/50",
                      isSelected && "bg-blue-50",
                      isOverdue && !isSelected && "bg-red-50/30",
                      "hover:bg-blue-50/50"
                    )}
                    onClick={() => onSelect(grn)}
                    data-testid={`grn-table-row-${grn.grn_id}`}
                  >
                    {/* Checkbox */}
                    <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleSelectOne(grn.grn_id, checked)}
                        aria-label={`Select ${grn.grn_number}`}
                      />
                    </TableCell>
                    
                    {/* GRN Number */}
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1.5 group/grn">
                        <span className="text-[#101957]">{grn.grn_number}</span>
                        <button
                          onClick={(e) => copyToClipboard(grn.grn_number, e)}
                          className="opacity-0 group-hover/grn:opacity-100 transition-opacity text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </TableCell>
                    
                    {/* Date */}
                    <TableCell className="text-sm text-gray-600">
                      {formatIST(grn.created_at, { includeTime: false })}
                    </TableCell>
                    
                    {/* PO Number */}
                    <TableCell className="text-sm">
                      {grn.po_number ? (
                        <span className="text-gray-700">{grn.po_number}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    
                    {/* Supplier */}
                    <TableCell>
                      {grn.supplier_name ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1.5 max-w-[150px]">
                                <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span className="truncate text-sm">{grn.supplier_name}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{grn.supplier_name}</p>
                              {grn.is_approved_supplier && (
                                <p className="text-green-400 text-xs flex items-center gap-1">
                                  <Check className="w-3 h-3" /> Approved Supplier
                                </p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    
                    {/* Lines */}
                    <TableCell>
                      <LineStatusSummary grn={grn} />
                    </TableCell>
                    
                    {/* Qty */}
                    <TableCell className="text-right text-sm font-medium">
                      {grn.total_quantity_received || 0}
                    </TableCell>
                    
                    {/* Status */}
                    <TableCell>
                      <StatusBadgeCompact status={grn.status} />
                    </TableCell>
                    
                    {/* Age */}
                    <TableCell>
                      <AgeBadge grn={grn} />
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => onSelect(grn)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => copyToClipboard(grn.grn_number, e)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy GRN #
                          </DropdownMenuItem>
                          {grn.tracking_number && (
                            <DropdownMenuItem onClick={(e) => copyToClipboard(grn.tracking_number, e)}>
                              <Truck className="w-4 h-4 mr-2" />
                              Copy Tracking
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {grn.status === 'DRAFT' && grn.line_count > 0 && (
                            <DropdownMenuItem className="text-green-600">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Complete Receipt
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Table Footer - Selection Summary */}
      {selectedIds.length > 0 && (
        <div className="px-4 py-2 bg-blue-50 border-t flex items-center justify-between text-sm">
          <span className="text-blue-700 font-medium">
            {selectedIds.length} of {grns.length} selected
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7 text-blue-600" onClick={() => onSelectionChange?.([])}>
              Clear Selection
            </Button>
            {/* Placeholder for bulk actions - Phase 6 */}
          </div>
        </div>
      )}
    </div>
  );
};

export default GRNTableView;
